import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { bookMedicationReviewLeadSchema, type BookMedicationReviewLeadInput } from "@dosa/validation";
import { Button, FieldLabel, FieldError, Input, Textarea, Alert, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@dosa/ui";

export function BookReviewPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<BookMedicationReviewLeadInput>({
    resolver: zodResolver(bookMedicationReviewLeadSchema),
    defaultValues: { preferredLanguage: "ar" },
  });

  const onSubmit = async () => {
    await new Promise((r) => setTimeout(r, 400));
    setSubmitted(true);
  };

  return (
    <div className="container-app max-w-xl py-16 lg:py-20">
      <h1 className="text-4xl font-semibold text-charcoal-900">{t("bookReview.title")}</h1>
      <p className="mt-3 text-lg text-charcoal-600">{t("bookReview.subtitle")}</p>

      {submitted ? (
        <Alert variant="success" className="mt-8" title={t("bookReview.success")} />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" noValidate>
          <div>
            <FieldLabel htmlFor="fullName">{t("contact.fullName")}</FieldLabel>
            <Input id="fullName" aria-invalid={!!errors.fullName} {...register("fullName")} />
            <FieldError>{errors.fullName?.message}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="email">{t("contact.email")}</FieldLabel>
            <Input id="email" type="email" aria-invalid={!!errors.email} {...register("email")} />
            <FieldError>{errors.email?.message}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="phone">{t("contact.phone")}</FieldLabel>
            <Input id="phone" aria-invalid={!!errors.phone} {...register("phone")} />
            <FieldError>{errors.phone?.message}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="preferredLanguage">{t("bookReview.preferredLanguage")}</FieldLabel>
            <Controller
              control={control}
              name="preferredLanguage"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} items={{ ar: t("common.arabic"), en: t("common.english") }}>
                  <SelectTrigger id="preferredLanguage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ar">{t("common.arabic")}</SelectItem>
                    <SelectItem value="en">{t("common.english")}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div>
            <FieldLabel htmlFor="notes">{t("common.notes")}</FieldLabel>
            <Textarea id="notes" rows={4} {...register("notes")} />
            <FieldError>{errors.notes?.message}</FieldError>
          </div>
          <Button type="submit" isLoading={isSubmitting} size="lg">
            {t("common.submit")}
          </Button>
        </form>
      )}
    </div>
  );
}
